import * as React from 'react';
import {
    Datagrid,
    FunctionField,
    List,
    NumberField,
    ReferenceField,
    TextField,
    TextInput,
    useDataProvider,
    useListContext,
    useNotify,
    useRedirect,
} from 'react-admin';
import {
    Box,
    Button,
    Checkbox,
    Radio,
    Typography,
} from '@material-ui/core';
import AdminPagination from '../AdminPagination';

const filters = [
    <TextInput source="all" label="Rechercher" alwaysOn />
];

const normalizeId = (value: any) =>
    String(value?.id || value?._id || value || '');

const EventContentSelectionGrid = ({
    eventId,
    field,
    children,
    eventRecord,
    setEventRecord,
}: any) => {
    const dataProvider: any = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const { ids, data } = useListContext();

    const records = (ids || [])
        .map((id: any) => data[id])
        .filter(Boolean);

    const [saving, setSaving] =
        React.useState(false);

    const selectedIds = (
        eventRecord?.[field] || []
    ).map(normalizeId);

    const selectedSet =
        new Set(selectedIds);

    const customMode =
        eventRecord?.content_mode === 'custom';

    const save = async (nextIds: string[]) => {
        setSaving(true);

        try {
            const response =
                await dataProvider.setEventContent(
                    eventId,
                    {
                        [field]: Array.from(
                            new Set(nextIds)
                        ),
                    }
                );

            setEventRecord(response.data);

            notify(
                'Configuration enregistrée',
                'success'
            );
        } catch (error) {
            notify(
                "Impossible d'enregistrer la configuration",
                'warning'
            );
        } finally {
            setSaving(false);
        }
    };

    const toggleOne = (
        record: any,
        checked: boolean
    ) => {
        const id = normalizeId(record);

        const next = new Set<string>(selectedIds as string[]);

        if (checked)
            next.add(id);
        else
            next.delete(id);

        save(Array.from(next));
    };

    const setCurrentPage = (
        checked: boolean
    ) => {
        const next = new Set<string>(selectedIds as string[]);

        records.forEach((record: any) => {
            const id = normalizeId(record);

            if (checked)
                next.add(id);
            else
                next.delete(id);
        });

        save(Array.from(next));
    };

    const selectedOnPage =
        records.filter((record: any) =>
            selectedSet.has(normalizeId(record))
        ).length;

    return (
        <>
            <Box
                mb={2}
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                style={{ gap: 12 }}
            >
                <Typography variant="body2">
                    {selectedIds.length} sélectionné(s)
                </Typography>

                <Typography
                    variant="body2"
                    color="textSecondary"
                >
                    Enregistrement automatique
                </Typography>

                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() =>
                        redirect(`/event/${eventId}`)
                    }
                >
                    Terminer et revenir à l’événement
                </Button>

                {!customMode && (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                    >
                        Passez d’abord l’événement en
                        mode Personnalisé.
                    </Typography>
                )}

                <Button
                    variant="outlined"
                    size="small"
                    disabled={
                        saving ||
                        !customMode ||
                        !records.length ||
                        selectedOnPage === records.length
                    }
                    onClick={() =>
                        setCurrentPage(true)
                    }
                >
                    Tout cocher cette page
                </Button>

                <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    disabled={
                        saving ||
                        !customMode ||
                        selectedOnPage === 0
                    }
                    onClick={() =>
                        setCurrentPage(false)
                    }
                >
                    Tout décocher cette page
                </Button>
            </Box>

            <Datagrid>
                <FunctionField
                    label="Sélection"
                    render={(record: any) => (
                        <Checkbox
                            checked={selectedSet.has(
                                normalizeId(record)
                            )}
                            disabled={
                                saving || !customMode
                            }
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                            onChange={(event) =>
                                toggleOne(
                                    record,
                                    event.target.checked
                                )
                            }
                        />
                    )}
                />

                {children}
            </Datagrid>
        </>
    );
};


const EventContentSelectorList = ({
    props,
    kind,
    field,
    title,
    sort,
    children,
}: any) => {
    const dataProvider: any = useDataProvider();
    const notify = useNotify();

    const [eventRecord, setEventRecord] =
        React.useState<any>(null);

    React.useEffect(() => {
        let mounted = true;

        dataProvider
            .getOne('event', { id: props.eventId })
            .then((response: any) => {
                if (mounted)
                    setEventRecord(response.data);
            })
            .catch(() => {
                if (mounted)
                    notify(
                        "Impossible de charger l'événement",
                        'warning'
                    );
            });

        return () => {
            mounted = false;
        };
    }, [dataProvider, props.eventId, notify]);

    if (!eventRecord)
        return (
            <Typography variant="body2">
                Chargement de la configuration…
            </Typography>
        );

    const customMode =
        eventRecord.content_mode === 'custom';

    const catalogId =
        eventRecord.catalog_id
            ? normalizeId(eventRecord.catalog_id)
            : '';

    const selectedCategoryIds = (
        eventRecord.content_interlude_category_ids || []
    ).map(normalizeId);

    const filter: any =
        kind === 'interlude'
            ? { category_is_demo: false }
            : { is_demo: false };

    if (customMode && catalogId) {
        filter.catalog_ids = catalogId;
    } else {
        filter.is_standard = true;

        if (kind !== 'interlude')
            filter.is_special = false;
    }

    if (
        kind === 'interlude' &&
        selectedCategoryIds.length
    ) {
        delete filter.category_is_demo;

        filter.category_ids =
            selectedCategoryIds.join(',');
    }

    return (
        <List
            {...props}
            title={title}
            filter={filter}
            filters={filters}
            actions={false}
            bulkActionButtons={false}
            sort={sort}
            perPage={50}
            pagination={<AdminPagination />}
            exporter={false}
        >
            <EventContentSelectionGrid
                eventId={props.eventId}
                field={field}
                eventRecord={eventRecord}
                setEventRecord={setEventRecord}
            >
                {children}
            </EventContentSelectionGrid>
        </List>
    );
};

export const EventInterludeCategorySelector = (
    props: any
) => (
    <EventContentSelectorList
        props={props}
        kind="category"
        field="content_interlude_category_ids"
        title="Configuration événement — Dossiers d’interludes"
        sort={{ field: 'name', order: 'ASC' }}
    >
        <TextField
            source="name"
            label="Nom"
        />
        <NumberField
            source="sounds_count"
            label="Sons"
        />
    </EventContentSelectorList>
);

export const EventInterludeSelector = (
    props: any
) => (
    <EventContentSelectorList
        props={props}
        kind="interlude"
        field="content_interlude_ids"
        title="Configuration événement — Sons Interlude"
        sort={{ field: 'category_id', order: 'ASC' }}
    >
        <ReferenceField
            label="Catégorie"
            source="category_id"
            reference="interludecategory"
        >
            <FunctionField
                render={(record: any) =>
                    record?.name || ''
                }
            />
        </ReferenceField>

        <TextField
            source="name"
            label="Nom"
        />
    </EventContentSelectorList>
);


const EventIntroVideoSelectionGrid = ({
    eventId,
    eventRecord,
    setEventRecord,
}: any) => {
    const dataProvider: any = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const { ids, data } = useListContext();

    const records = (ids || [])
        .map((id: any) => data[id])
        .filter(Boolean);

    const [saving, setSaving] =
        React.useState(false);

    const selectedId =
        eventRecord?.content_intro_video_id
            ? normalizeId(
                eventRecord.content_intro_video_id
            )
            : '';

    const customMode =
        eventRecord?.content_mode === 'custom';

    const save = async (
        nextId: string | null
    ) => {
        setSaving(true);

        try {
            const response =
                await dataProvider.setEventContent(
                    eventId,
                    {
                        content_intro_video_id:
                            nextId,
                    }
                );

            setEventRecord(response.data);

            notify(
                nextId
                    ? 'Introduction enregistrée'
                    : 'Introduction personnalisée retirée',
                'success'
            );
        } catch (error) {
            notify(
                "Impossible d'enregistrer l'introduction",
                'warning'
            );
        } finally {
            setSaving(false);
        }
    };

    const selectedRecord =
        records.find(
            (record: any) =>
                normalizeId(record) === selectedId
        );

    return (
        <>
            <Box
                mb={2}
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                style={{ gap: 12 }}
            >
                <Typography variant="body2">
                    {selectedId
                        ? `Introduction sélectionnée : ${
                            selectedRecord?.name ||
                            '1 vidéo'
                        }`
                        : 'Aucune introduction personnalisée sélectionnée'}
                </Typography>

                <Typography
                    variant="body2"
                    color="textSecondary"
                >
                    Enregistrement automatique
                </Typography>

                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() =>
                        redirect(`/event/${eventId}`)
                    }
                >
                    Terminer et revenir à l’événement
                </Button>

                {selectedId && (
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        disabled={
                            saving ||
                            !customMode
                        }
                        onClick={() =>
                            save(null)
                        }
                    >
                        Retirer la sélection
                    </Button>
                )}

                {!customMode && (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                    >
                        Passez d’abord l’événement en
                        mode Personnalisé.
                    </Typography>
                )}
            </Box>

            <Datagrid>
                <FunctionField
                    label="Sélection"
                    render={(record: any) => {
                        const id =
                            normalizeId(record);

                        return (
                            <Radio
                                checked={
                                    selectedId === id
                                }
                                disabled={
                                    saving ||
                                    !customMode
                                }
                                onClick={(event) =>
                                    event.stopPropagation()
                                }
                                onChange={() =>
                                    save(id)
                                }
                            />
                        );
                    }}
                />

                <TextField
                    source="name"
                    label="Nom"
                />

                <TextField
                    source="video"
                    label="ID vidéo"
                />
            </Datagrid>
        </>
    );
};


export const EventIntroVideoSelector = (
    props: any
) => {
    const dataProvider: any = useDataProvider();
    const notify = useNotify();

    const [eventRecord, setEventRecord] =
        React.useState<any>(null);

    React.useEffect(() => {
        let mounted = true;

        dataProvider
            .getOne('event', {
                id: props.eventId,
            })
            .then((response: any) => {
                if (mounted)
                    setEventRecord(
                        response.data
                    );
            })
            .catch(() => {
                if (mounted)
                    notify(
                        "Impossible de charger l'événement",
                        'warning'
                    );
            });

        return () => {
            mounted = false;
        };
    }, [
        dataProvider,
        props.eventId,
        notify,
    ]);

    if (!eventRecord)
        return (
            <Typography variant="body2">
                Chargement de la configuration…
            </Typography>
        );

    const customMode =
        eventRecord.content_mode === 'custom';

    const catalogId =
        eventRecord.catalog_id
            ? normalizeId(
                eventRecord.catalog_id
            )
            : '';

    const filter: any = {
        is_demo: false,
    };

    if (
        customMode &&
        catalogId
    ) {
        filter.catalog_ids =
            catalogId;
    } else {
        filter.is_standard = true;
    }

    return (
        <List
            {...props}
            title="Configuration événement — Introduction"
            filter={filter}
            filters={filters}
            actions={false}
            bulkActionButtons={false}
            sort={{
                field: 'name',
                order: 'ASC',
            }}
            perPage={50}
            pagination={<AdminPagination />}
            exporter={false}
        >
            <EventIntroVideoSelectionGrid
                eventId={props.eventId}
                eventRecord={eventRecord}
                setEventRecord={
                    setEventRecord
                }
            />
        </List>
    );
};


export const EventMedleySelector = (
    props: any
) => (
    <EventContentSelectorList
        props={props}
        kind="medley"
        field="content_medley_ids"
        title="Configuration événement — Medleys"
        sort={{ field: 'name', order: 'ASC' }}
    >
        <TextField
            source="name"
            label="Nom"
        />
    </EventContentSelectorList>
);
